import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import useNullAccountData from '../hooks/useNullAccountData';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const NullAccountAnalytics = () => {
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTimeframe, setActiveTimeframe] = useState('all');
  const [filteredData, setFilteredData] = useState([]);
  const [stats, setStats] = useState({
    totalBurned: 0,
    averageDaily: 0,
    daysCovered: 0
  });
  const chartRef = useRef();

  // Use independent null account data hook
  const {
    totalBurned,
    burnsToday,
    burnsByDay,
    isLoading: isLoadingNullData,
    refreshData: refreshNullData
  } = useNullAccountData();

  // Prepare chart data for null account
  const loadNullAccountAnalytics = useCallback(() => {
    try {
      setIsLoading(true);

      if (!burnsByDay || Object.keys(burnsByDay).length === 0) {
        setChartData({
          labels: ['No Data'],
          datasets: [{
            label: 'Null Account Burns',
            data: [0],
            borderColor: '#ff6b6b',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            fill: true,
            tension: 0.4
          }]
        });
        return;
      }

      // Filter data based on active timeframe
      const now = Math.floor(Date.now() / 1000);
      let daysBack;

      switch (activeTimeframe) {
        case '7d': daysBack = 7; break;
        case '30d': daysBack = 30; break;
        case '90d': daysBack = 90; break;
        case 'all': daysBack = 0; break;
        default: daysBack = 30;
      }

      let timeframeCutoff;
      if (daysBack === 0) {
        timeframeCutoff = 0;
      } else {
        timeframeCutoff = now - (daysBack * 24 * 60 * 60);
      }

      // Get and sort burn data for the timeframe
      let filteredData = Object.keys(burnsByDay)
        .map(timestamp => ({
          timestamp: parseInt(timestamp),
          burns: burnsByDay[timestamp]
        }))
        .filter(item => item.timestamp >= timeframeCutoff)
        .sort((a, b) => a.timestamp - b.timestamp);

      // Fill gaps to today
      if (filteredData.length > 0) {
        const lastDataPoint = filteredData[filteredData.length - 1].timestamp;
        const today = Math.floor(Date.now() / 1000);
        const todayMidnight = today - (today % 86400);

        if (lastDataPoint < todayMidnight) {
          let currentDay = lastDataPoint + 86400;
          while (currentDay <= todayMidnight) {
            filteredData.push({ timestamp: currentDay, burns: { steem: 0, sbd: 0, vests: 0 } });
            currentDay += 86400;
          }
        }
      }

      setFilteredData(filteredData);

      if (filteredData.length === 0) {
        setChartData({
          labels: ['No Data'],
          datasets: [{
            label: 'STEEM Burned',
            data: [0],
            borderColor: '#ff6b6b',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            fill: true,
            tension: 0.4
          }]
        });
        return;
      }

      // Prepare chart labels
      const labels = filteredData.map(item => {
        const date = new Date(item.timestamp * 1000);
        if (daysBack <= 7) {
          return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          });
        } else if (daysBack <= 30) {
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
        } else {
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
        }
      });

      // Extract STEEM amounts from daily burn data
      const burnAmounts = filteredData.map(item => parseFloat(item.burns.steem) || 0);

      // Calculate cumulative STEEM burns
      let cumulativeAmounts = [];
      let runningTotal = 0;
      cumulativeAmounts = filteredData.map(item => {
        runningTotal += parseFloat(item.burns.steem) || 0;
        return runningTotal;
      });

      const datasets = [{
        label: 'Daily Burns',
        data: burnAmounts,
        borderColor: '#ff6b6b',
        backgroundColor: function(context) {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(255, 107, 107, 0.3)');
          gradient.addColorStop(0.5, 'rgba(255, 107, 107, 0.15)');
          gradient.addColorStop(1, 'rgba(255, 107, 107, 0.02)');
          return gradient;
        },
        fill: true,
        tension: 0.1,
        pointRadius: function() {
          return daysBack <= 7 ? 6 : daysBack <= 30 ? 5 : 4;
        },
        pointHoverRadius: function() {
          return daysBack <= 7 ? 10 : daysBack <= 30 ? 8 : 6;
        },
        pointBackgroundColor: '#ff6b6b',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 3,
        pointHoverBorderWidth: 4,
        pointHoverBackgroundColor: '#ff4444',
        pointHoverBorderColor: '#ffffff',
        borderWidth: 3,
        order: 1
      }];

      // Add cumulative line
      if (cumulativeAmounts.length > 0) {
        datasets.push({
          label: 'Cumulative Total',
          data: cumulativeAmounts,
          borderColor: '#ff9100',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.3,
          pointRadius: daysBack <= 7 ? 4 : 3,
          pointHoverRadius: daysBack <= 7 ? 8 : 6,
          pointBackgroundColor: '#ff9100',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 3,
          pointHoverBackgroundColor: '#ff7700',
          pointHoverBorderColor: '#ffffff',
          borderWidth: 2.5,
          borderDash: [8, 4],
          borderCapStyle: 'round',
          order: 2
        });
      }

      setChartData({
        labels: labels,
        datasets: datasets
      });
    } catch (error) {
      // Error loading null account analytics
    } finally {
      setIsLoading(false);
    }
  }, [activeTimeframe, burnsByDay]);

  // Calculate statistics
  const calculateStats = useCallback(() => {
    if (!filteredData || filteredData.length === 0) {
      setStats({
        totalBurned: 0,
        averageDaily: 0,
        daysCovered: 0
      });
      return;
    }

    const total = filteredData.reduce((sum, item) => sum + (parseFloat(item.burns.steem) || 0), 0);
    const averageDaily = total / filteredData.length;
    const daysCovered = filteredData.length;

    setStats({
      totalBurned: total,
      averageDaily: averageDaily,
      daysCovered: daysCovered
    });
  }, [filteredData]);

  useEffect(() => {
    loadNullAccountAnalytics();
    calculateStats();
  }, [activeTimeframe, loadNullAccountAnalytics, calculateStats]);

  return (
    <div className="analytics-main-card">
      {/* Header Section */}
      <div className="analytics-header">
        <div className="header-left">
          <div className="icon-container">
            <div className="icon-glow"></div>
            <i className="fas fa-ban"></i>
          </div>
          <div className="header-text">
            <h3>Null Account Burns</h3>
            <p>Direct burns to null account</p>
          </div>
        </div>
        <div className="header-right">
          <button
            className="refresh-analytics-btn"
            onClick={() => refreshNullData()}
            disabled={isLoading || isLoadingNullData}
          >
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <div className="burn-stats-row">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-fire"></i></div>
          <div className="stat-info">
            <span className="stat-value">
              {isLoading || isLoadingNullData ? 'Loading...' : `${parseFloat(totalBurned.steem).toLocaleString('en-US', { maximumFractionDigits: 2 })} STEEM`}
            </span>
            <span className="stat-label">TOTAL BURNED (STEEM)</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-coins"></i></div>
          <div className="stat-info">
            <span className="stat-value">
              {isLoading || isLoadingNullData ? 'Loading...' : `${parseFloat(totalBurned.sbd).toLocaleString('en-US', { maximumFractionDigits: 2 })} SBD`}
            </span>
            <span className="stat-label">TOTAL BURNED (SBD)</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-calculator"></i></div>
          <div className="stat-info">
            <span className="stat-value">
              {isLoading ? 'Loading...' : `${parseFloat(stats.averageDaily).toLocaleString('en-US', { maximumFractionDigits: 2 })} STEEM/day`}
            </span>
            <span className="stat-label">AVERAGE RATE</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-hourglass-end"></i></div>
          <div className="stat-info">
            <span className="stat-value">
              {isLoading || isLoadingNullData ? 'Loading...' : burnsToday.steem}
            </span>
            <span className="stat-label">24H BURNED (STEEM)</span>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="chart-section">
        <div className="chart-header">
          <div className="chart-header-main">
            <h4 className="chart-title">
              <i className="fas fa-chart-area"></i>
              Null Account Burn Analysis
            </h4>
            <div className="chart-subtitle">
              Real-time tracking of burns sent to null account
            </div>
          </div>
          <div className="chart-info-badges">
            {chartData && chartData.datasets && (
              <span className="data-points-badge">
                <i className="fas fa-chart-line"></i>
                {chartData.datasets[0]?.data?.length || 0} Data Points
              </span>
            )}
            {stats.totalBurned > 0 && (
              <span className="total-badge success">
                <i className="fas fa-fire"></i>
                {stats.totalBurned >= 1000000 
                  ? `${(stats.totalBurned/1000000).toFixed(2)}M` 
                  : stats.totalBurned >= 1000 
                  ? `${(stats.totalBurned/1000).toFixed(1)}K` 
                  : stats.totalBurned.toFixed(0)
                } STEEM
              </span>
            )}
          </div>
        </div>

        <div className="modern-chart-container">
          <div className="timeframe-selector">
            {['7d', '30d', '90d', 'all'].map((tf) => (
              <button
                key={tf}
                className={`time-btn ${activeTimeframe === tf ? 'active' : ''}`}
                onClick={() => setActiveTimeframe(tf)}
              >
                {tf.toUpperCase()}
              </button>
            ))}
          </div>

          {isLoading || isLoadingNullData ? (
            <div className="chart-overlay">
              <div className="chart-loading-content">
                <div className="loading-spinner-advanced">
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                </div>
                <div className="loading-text">
                  <h4>Loading Null Account Analytics</h4>
                  <p>Fetching burn transactions...</p>
                </div>
              </div>
            </div>
          ) : (
            chartData ? (
              <>
                <div className="chart-wrapper">
                  <Line
                    ref={chartRef}
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          labels: {
                            color: '#fff',
                            boxWidth: 12,
                            padding: 15,
                            font: {
                              size: 12
                            }
                          }
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          padding: 12,
                          titleColor: '#fff',
                          bodyColor: '#fff',
                          borderColor: '#ff6b6b',
                          borderWidth: 1,
                          displayColors: true,
                          callbacks: {
                            label: function(context) {
                              return `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-US', { maximumFractionDigits: 2 })} STEEM`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          ticks: {
                            color: '#fff',
                            callback: function(value) {
                              return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
                            }
                          },
                          grid: {
                            color: 'rgba(255, 255, 255, 0.1)',
                            drawBorder: false
                          }
                        },
                        x: {
                          ticks: {
                            color: '#fff',
                            maxRotation: 45,
                            minRotation: 0
                          },
                          grid: {
                            color: 'rgba(255, 255, 255, 0.05)',
                            drawBorder: false
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="chart-footer">
                  <div className="data-legend">
                    <h5>Data Types:</h5>
                    <div className="legend-items">
                      <div className="legend-item">
                        <div className="legend-indicator real-data"></div>
                        <span><strong>Daily Burns (Red):</strong> Actual STEEM burned to null account on specific dates</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-indicator calculated-cumulative"></div>
                        <span><strong>Cumulative Total (Orange):</strong> Running sum of all null account burns</span>
                      </div>
                    </div>
                  </div>
                  <div className="chart-insights">
                    <div className="insight-item">
                      <span className="insight-label">Account:</span>
                      <span className="insight-value">
                        <i className="fas fa-circle-notch"></i>
                        Null Account
                      </span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Last Update:</span>
                      <span className="insight-value">
                        {new Date().toLocaleTimeString('en-US', { 
                          hour12: true, 
                          hour: '2-digit', 
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Data Quality:</span>
                      <span className="insight-value">
                        <i className="fas fa-check-circle text-success"></i>
                        Live Blockchain Data
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="chart-overlay">
                <div className="chart-empty-state">
                  <div className="empty-state-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <h4>No Chart Data Available</h4>
                  <p>Unable to load null account analytics data.</p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default NullAccountAnalytics;
